import { useState, useEffect, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useTabVisibility } from './useTabVisibility'

export function useBillingStatus() {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)
    const [billingStatus, setBillingStatus] = useState<any>(null)
    const isVisible = useTabVisibility()
    const lastFetchTime = useRef<number>(0)

    const fetchBillingStatus = async (options = { showLoading: true, forceRefresh: false }) => {
        try {
            // Avoid excessive fetching by checking if time since last fetch is < 5 seconds
            // Skip this check if forceRefresh is true
            const now = Date.now()
            if (!options.forceRefresh && now - lastFetchTime.current < 5000) {
                return
            }

            lastFetchTime.current = now

            // Only show loading indicator for explicit user actions, not background polling
            if (options.showLoading) {
                setLoading(true)
            }
            setError(null)

            // Get account ID from current user's personal account
            const { data: account } = await supabase.rpc('get_personal_account') as { data: { account_id: string } | null }

            if (!account?.account_id) {
                throw new Error('No account found')
            }

            const session = await supabase.auth.getSession()
            if (!session.data.session) {
                throw new Error('No session found')
            }

            // Use the worker to get the billing status
            const workerUrl = import.meta.env.VITE_WORKERS_URL
            const url = options.forceRefresh
                ? `${workerUrl}/billing-functions/get_current_billing_status?force_refresh=true`
                : `${workerUrl}/billing-functions/get_current_billing_status`

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${session.data.session.access_token}`
                },
                body: JSON.stringify({ account_id: account.account_id })
            })

            if (!response.ok) {
                throw new Error('Failed to fetch billing status')
            }

            const data = await response.json()

            // Only update state if data actually changed to avoid unnecessary rerenders
            if (JSON.stringify(data) !== JSON.stringify(billingStatus)) {
                setBillingStatus(data)
            }
        } catch (err) {
            setError(err instanceof Error ? err : new Error('Failed to fetch billing status'))
            console.error('Error fetching billing status:', err)
        } finally {
            if (options.showLoading) {
                setLoading(false)
            }
        }
    }

    const handleManageSubscription = async () => {
        try {
            setLoading(true)
            const { data: account } = await supabase.rpc('get_personal_account') as { data: { account_id: string } | null }

            if (!account?.account_id) {
                throw new Error('No account found')
            }

            const session = await supabase.auth.getSession()
            if (!session.data.session) {
                throw new Error('No session found')
            }

            const workerUrl = import.meta.env.VITE_WORKERS_URL
            const response = await fetch(`${workerUrl}/billing-functions/get_billing_portal_url`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${session.data.session.access_token}`
                },
                body: JSON.stringify({
                    account_id: account.account_id,
                    return_url: `${window.location.origin}/settings`
                })
            })

            if (!response.ok) {
                throw new Error('Failed to get billing portal URL')
            }

            const data = await response.json()
            if (data?.url) {
                window.location.href = data.url
            }
        } catch (error) {
            console.error('Error accessing billing portal:', error)
        } finally {
            setLoading(false)
        }
    }

    const handleChangePlan = async (priceId: string, interval: 'monthly' | 'yearly', options?: { billing_cycle_anchor?: 'unchanged' | 'now' }) => {
        try {
            setLoading(true)
            console.log('Changing plan to:', priceId, interval, options)
            const { data: account } = await supabase.rpc('get_personal_account') as { data: { account_id: string } | null }

            if (!account?.account_id) {
                throw new Error('No account found')
            }

            const session = await supabase.auth.getSession()
            if (!session.data.session) {
                throw new Error('No session found')
            }

            const workerUrl = import.meta.env.VITE_WORKERS_URL
            const response = await fetch(`${workerUrl}/billing-functions/change_subscription`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${session.data.session.access_token}`
                },
                body: JSON.stringify({
                    args: {
                        new_plan_id: priceId,
                        billing_interval: interval,
                        current_plan_id: billingStatus?.subscription?.price_id // Add current plan ID for verification
                    }
                })
            })

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}))
                throw new Error(errorData.error || 'Failed to change plan')
            }

            const data = await response.json()
            // Explicitly show loading state when user changes plan
            await fetchBillingStatus({ showLoading: true, forceRefresh: false })
            return data
        } catch (error) {
            console.error('Error changing plan:', error)
            return { error }
        } finally {
            setLoading(false)
        }
    }

    // Initial fetch
    useEffect(() => {
        fetchBillingStatus()
    }, [])

    // Poll for updates every 10 seconds when tab is visible
    useEffect(() => {
        if (!isVisible) return

        const interval = setInterval(() => {
            // Use silent update for background polling (no loading state)
            fetchBillingStatus({ showLoading: false, forceRefresh: false })
        }, 10000) // Poll every 10 seconds

        return () => clearInterval(interval)
    }, [isVisible])

    // Refresh when tab becomes visible
    useEffect(() => {
        if (isVisible) {
            // Use silent update when returning to tab
            fetchBillingStatus({ showLoading: false, forceRefresh: false })
        }
    }, [isVisible])

    return {
        loading,
        error,
        billingStatus,
        handleManageSubscription,
        handleChangePlan,
        refresh: fetchBillingStatus
    }
} 